import {
  clampRate, CLOSE_CHECKS, payoutBasis, payoutOf, prevMonth, settlementTotals,
  type ClosingBook, type CloseCheck, type MeetingMinutes,
} from '@selfon/domain/browser';
import { useMemo, useState } from 'react';
import { api, type Bill, type StaffRow } from '../api';
import { useAuth } from '../auth/AuthContext';
import { Alert, Empty, errorText, Loading, MonthPicker, PageHeader, useLoad, ViewDialog } from '../components/ui';
import { thisMonthKst, won } from '../format';
import { MinutesForm } from './ClosingMinutes';
import { PaymentImport } from './PaymentImport';
import { PayrollLedger } from './Payroll';
import './accounting.css';

/*
 * 결산 회계 세무(원장): 수업별 결산 · 결산 회의록 · 사업소득 지급 분배 · 급상여대장·임금명세서 + 월 마감 체크.
 * 수업별 결산 숫자는 수납 현황과 같은 계산(서버 monthBill)에서만 나온다. 지급·급여는 계산식만 두고 금액은 이 화면에서 입력한다.
 * 이 화면의 어떤 동작도 실제 회계 마감·세무 신고·지급을 실행하지 않는다.
 */

const TABS = [['class', '수업별 결산'], ['minutes', '결산 회의록'], ['payout', '사업소득지급분배'], ['payroll', '급상여대장·임금명세서']] as const;
type Tab = (typeof TABS)[number][0];
const nf = (v: number) => v.toLocaleString('ko-KR');
const monthTitle = (m: string) => `${m.slice(0, 4)}년 ${Number(m.slice(5, 7))}월`;

export function Accounting() {
  const { me } = useAuth();
  const centerId = me!.centerId!;
  const [month, setMonth] = useState(thisMonthKst());
  const [tab, setTab] = useState<Tab>('class');
  const q = useLoad(async () => {
    const [billing, ss, st, cl, center] = await Promise.all([
      api.billing(centerId, month), api.sessions(centerId, month), api.staff(centerId), api.closing(centerId, month), api.center(centerId),
    ]);
    return { month, bills: billing.bills, sessions: ss.sessions.filter((s) => s.status !== 'CANCELLED'), staff: st.staff, closing: cl.closing, centerName: center.center.name };
  }, [centerId, month]);
  // 저장 후 받은 최신 기록(버전 포함). 달이 바뀌면 불러온 값을 쓴다.
  const [saved, setSaved] = useState<{ month: string; book: ClosingBook } | null>(null);
  const d = q.data?.month === month ? q.data : null;
  const book = saved?.month === month ? saved.book : d?.closing ?? null;
  const totals = useMemo(() => settlementTotals(d?.bills ?? []), [d]);
  const checks = book?.checks ?? [];
  const save = async (b: { checks?: CloseCheck[]; minutes?: MeetingMinutes }) => {
    const { closing } = await api.saveClosing(centerId, { month, expectedVersion: book?.version ?? 0, ...b });
    setSaved({ month, book: closing });
  };

  return (
    <div className="stack acct">
      <PageHeader eyebrow="MONTHLY CLOSE" title="결산 회계 세무" sub="수업 실적에서 지급 검토까지, 한 달의 흐름을 한곳에서." actions={<MonthPicker value={month} onChange={setMonth} />} />
      <div className="bs-tabs acct-tabs" role="tablist" aria-label="결산 보기">
        {TABS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={`bs-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {q.error && <Alert>{q.error}</Alert>}
      {!d && !q.error && <Loading />}
      {d && (
        <div className="acct-layout">
          <div className="acct-main">
            <div className="kpis">
              <div className="kpi"><span>수업 청구액</span><strong>{won(totals.billed)}</strong><small>{d.bills.length}명 · 수납 현황과 같은 계산</small></div>
              <div className="kpi"><span>수납액</span><strong>{won(totals.collected)}</strong><small>환불·취소 반영 순액</small></div>
              <div className={`kpi${totals.outstanding > 0 ? ' kpi-warn' : ''}`}><span>미수금</span><strong>{won(totals.outstanding)}</strong><small>청구 − 수납</small></div>
              <div className="kpi"><span>마감 검토</span><strong>{checks.length}/{CLOSE_CHECKS.length}</strong><small>오른쪽 월 마감 체크</small></div>
            </div>
            {tab === 'class' && <ClassSettlement bills={d.bills} sessions={d.sessions} staff={d.staff} month={month} centerId={centerId} onImported={q.reload} />}
            {tab === 'minutes' && <MinutesForm key={month} month={month} initial={book?.minutes ?? null} history={book?.history ?? []} staff={d.staff} centerName={d.centerName}
              loadPrev={async () => (await api.closing(centerId, prevMonth(month))).closing?.minutes ?? null} onSave={(m) => save({ minutes: m })} />}
            {tab === 'payout' && <Payout bills={d.bills} sessions={d.sessions} staff={d.staff} />}
            {tab === 'payroll' && <PayrollLedger centerId={centerId} centerName={d.centerName} month={month} />}
          </div>
          <CloseChecklist month={month} checks={checks} onSave={(c) => save({ checks: c })} />
        </div>
      )}
    </div>
  );
}

/* ---------------- 월 마감 체크 ---------------- */

function CloseChecklist({ month, checks, onSave }: { month: string; checks: CloseCheck[]; onSave: (c: CloseCheck[]) => Promise<void> }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 누르면 바로 표시하고(저장 중), 저장에 실패하면 되돌린다
  const [pending, setPending] = useState<CloseCheck[] | null>(null);
  const shown = pending ?? checks;
  const toggle = async (k: CloseCheck) => {
    const next = shown.includes(k) ? shown.filter((x) => x !== k) : [...shown, k];
    setBusy(true); setError(null); setPending(next);
    try { await onSave(next); } catch (e) { setError(errorText(e)); } finally { setPending(null); setBusy(false); }
  };
  const pct = Math.round((shown.length / CLOSE_CHECKS.length) * 100);
  return (
    <aside className="panel acct-side" aria-labelledby="close-title">
      <div className="panel-head"><h2 id="close-title">월 마감 체크</h2><span className="muted small">{monthTitle(month)}</span></div>
      <ul className="plain acct-checks">
        {CLOSE_CHECKS.map(([k, l]) => (
          <li key={k}><label><input type="checkbox" checked={shown.includes(k)} disabled={busy} onChange={() => toggle(k)} /> {l}</label></li>
        ))}
      </ul>
      <div className="acct-progress">
        <progress max={CLOSE_CHECKS.length} value={shown.length} aria-label="마감 검토 진행률" />
        <span className="small">{shown.length}/{CLOSE_CHECKS.length} 완료 · {pct}%</span>
      </div>
      {error && <Alert>{error}</Alert>}
      <p className="hint">체크 완료는 실제 회계 마감이나 세무 신고를 실행하지 않습니다.</p>
      <section aria-labelledby="close-order">
        <h3 id="close-order">자료 확인 순서</h3>
        <ol className="acct-order">
          <li>일정·출결에서 출석·결석·취소 내역을 확인합니다.</li>
          <li>수납 현황에서 수납·환불과 미수금을 대조합니다.</li>
          <li>계약별 지급 기준으로 담당자별 지급 예정액을 검토합니다.</li>
          <li>결산 회의록을 작성하고 담당자가 최종 검토합니다.</li>
        </ol>
      </section>
    </aside>
  );
}

/* ---------------- 수업별 결산 ---------------- */

interface SessionLite { clientId: string; therapistId: string; program: string }

function ClassSettlement({ bills, sessions, staff, month, centerId, onImported }: { bills: Bill[]; sessions: SessionLite[]; staff: StaffRow[]; month: string; centerId: string; onImported: () => void }) {
  const [query, setQuery] = useState('');
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const name = useMemo(() => new Map(staff.map((s) => [s.userId, s.name])), [staff]);
  const rows = useMemo(() => bills.map((b) => {
    const mine = sessions.filter((s) => s.clientId === b.clientId);
    return {
      ...b, programs: [...new Set(mine.map((s) => s.program))].join(', ') || '-',
      teachers: [...new Set(mine.map((s) => name.get(s.therapistId) ?? '담당 미지정'))].join(', ') || '담당 미지정',
      unit: b.sessionCount ? Math.round(b.gross / b.sessionCount) : 0, diff: b.required - b.paid.total,
    };
  }), [bills, sessions, name]);
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const view = rows.filter((r) => words.every((w) => `${r.name} ${r.programs} ${r.teachers}`.toLowerCase().includes(w)));
  const open = rows.find((r) => r.clientId === openId) ?? null;
  return (
    <section className="stack" aria-labelledby="class-title">
      <div className="row">
        <h2 id="class-title">수업별 결산</h2>
        <label className="acct-search"><span className="sr-only">수업 검색</span><input type="search" placeholder="이용자, 수업, 담당 선생님" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
        <button type="button" className="btn btn-outline" onClick={() => { setNotice(null); setImporting(true); }}>결제내역 엑셀 올리기</button>
      </div>
      {notice && <Alert kind="ok">{notice}</Alert>}
      <PaymentImport centerId={centerId} month={month} open={importing} onClose={() => setImporting(false)} onDone={(msg) => { setNotice(msg); onImported(); }} />
      {view.length === 0 ? <Empty>{rows.length ? '검색 조건에 맞는 수업이 없습니다.' : `${monthTitle(month)}에 결산할 수업이 없습니다.`}</Empty> : (
        <div className="table-wrap">
          <table className="table acct-table">
            <caption className="sr-only">{monthTitle(month)} 수업별 결산</caption>
            <thead><tr><th scope="col">수업 / 담당</th><th scope="col" className="num">회기</th><th scope="col" className="num">청구액</th><th scope="col" className="num">수납액</th><th scope="col" className="num">미수금</th><th scope="col"><span className="sr-only">상세</span></th></tr></thead>
            <tbody>
              {view.map((r) => (
                <tr key={r.clientId}>
                  <td><strong>{r.name}</strong> <span className="muted small">{r.programs}</span><div className="muted small">{r.teachers}</div></td>
                  <td className="num">{r.sessionCount}회</td>
                  <td className="num">{nf(r.required)}</td>
                  <td className="num">{nf(r.paid.total)}</td>
                  <td className={`num${r.diff ? ' acct-warn' : ''}`}>{nf(r.diff)}{r.diff < 0 && <span className="small"> (초과)</span>}</td>
                  <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpenId(r.clientId)} aria-label={`${r.name} 결산 상세`}>상세</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ViewDialog title={open ? `${open.name} · ${monthTitle(month)} 결산` : '결산 상세'} open={Boolean(open)} onClose={() => setOpenId(null)}>
        {open && (
          <dl className="acct-detail">
            <div><dt>수업 / 담당</dt><dd>{open.programs} · {open.teachers}</dd></div>
            <div><dt>청구 산식</dt><dd>{open.sessionCount}회 × {won(open.unit)} = {won(open.gross)}</dd></div>
            <div><dt>할인</dt><dd>{won(open.discount)}</dd></div>
            <div><dt>청구액</dt><dd>{won(open.required)}</dd></div>
            <div><dt>수납액</dt><dd>{won(open.paid.total)} <span className="muted small">(바우처 {nf(open.paid.VOUCHER)} · 본인부담 {nf(open.paid.SELF_PAY)} · 추가부담 {nf(open.paid.EXTRA)})</span></dd></div>
            <div><dt>미수금</dt><dd className={open.diff ? 'acct-warn' : ''}>{won(open.diff)}</dd></div>
            <div><dt>출석 완료</dt><dd>{open.completedCount} / {open.sessionCount}회 (취소 제외)</dd></div>
          </dl>
        )}
      </ViewDialog>
    </section>
  );
}

/* ---------------- 사업소득 지급 분배 ---------------- */

function Payout({ bills, sessions, staff }: { bills: Bill[]; sessions: SessionLite[]; staff: StaffRow[] }) {
  const basis = useMemo(() => payoutBasis(bills, sessions), [bills, sessions]);
  const people = staff.filter((s) => basis.has(s.userId) || (s.active && s.role === 'THERAPIST'));
  const [all, setAll] = useState('40');
  const [rates, setRates] = useState<Record<string, string>>({});
  const rateOf = (id: string) => clampRate(rates[id] ?? all);
  const rows = people.map((s) => ({ s, basis: basis.get(s.userId) ?? 0, pay: payoutOf(basis.get(s.userId) ?? 0, rateOf(s.userId)) }));
  const total = rows.reduce((a, r) => ({ basis: a.basis + r.basis, pay: a.pay + r.pay }), { basis: 0, pay: 0 });
  return (
    <section className="stack" aria-labelledby="payout-title">
      <div className="row">
        <h2 id="payout-title">사업소득 지급 분배</h2>
        <label className="acct-rate-all">전체 배분율 <input type="number" inputMode="decimal" min={0} max={100} step={0.1} value={all} onChange={(e) => { setAll(e.target.value); setRates({}); }} /> %</label>
      </div>
      <p className="hint">수납 기준액 = 이용자별 수납액을 그 달 담당 회기 수 비율로 나눈 금액. 세전 지급 예정액 = 수납 기준액 × 배분율(원 미만 버림).</p>
      <div className="table-wrap">
        <table className="table acct-table">
          <caption className="sr-only">담당자별 세전 지급 예정액</caption>
          <thead><tr><th scope="col">담당자</th><th scope="col" className="num">수납 기준액</th><th scope="col" className="num">배분율(%)</th><th scope="col" className="num">세전 지급 예정액</th></tr></thead>
          <tbody>
            {rows.map(({ s, basis: b, pay }) => (
              <tr key={s.userId}>
                <td>{s.name} <span className="muted small">{s.title}</span></td>
                <td className="num">{nf(b)}</td>
                <td className="num"><input className="acct-rate" type="number" inputMode="decimal" min={0} max={100} step={0.1} aria-label={`${s.name} 배분율`} value={rates[s.userId] ?? all} onChange={(e) => setRates({ ...rates, [s.userId]: e.target.value })} /></td>
                <td className="num"><strong>{nf(pay)}</strong></td>
              </tr>
            ))}
            <tr className="acct-total"><th scope="row">합계</th><td className="num">{nf(total.basis)}</td><td /><td className="num"><strong>{nf(total.pay)}</strong></td></tr>
          </tbody>
        </table>
      </div>
      <p className="acct-caution" role="note">검토용 계산입니다. 배분율은 저장되지 않으며, 소득 구분·원천징수·신고 대상은 세무 담당자와 별도로 검토해 주세요.</p>
    </section>
  );
}

/* ---------------- 급여명세서 ---------------- */
